/**
 * Payment Summary Component
 * Displays summary of payment being made
 */

import { type BalanceResponse } from '@/types/api.types';
import { formatCurrency } from '@/utils/formatters';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/common';
import { cn } from '@/lib/utils';

interface PaymentSummaryProps {
  balance: BalanceResponse;
  paymentAmount: number;
}

export default function PaymentSummary({ balance, paymentAmount }: PaymentSummaryProps) {
  const isPaidInFull = paymentAmount >= balance.totalBalance;

  return (
    <div className="space-y-4">
      {/* Payment Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Summary</CardTitle>
        </CardHeader>
        <CardBody className="!p-0">
          <table className="w-full">
            <thead className="sr-only">
              <tr>
                <th>Description</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {/* Policy Information */}
              <tr className="border-b border-border">
                <td className="px-4 py-3 text-sm text-text-muted">Policy Number</td>
                <td className="px-4 py-3 text-sm font-medium text-text text-right">
                  {balance.policyNumber}
                </td>
              </tr>

              <tr className="border-b border-border bg-surface/50">
                <td className="px-4 py-3 text-sm text-text-muted">Insured</td>
                <td className="px-4 py-3 text-sm font-medium text-text text-right">
                  {balance.insuredName}
                </td>
              </tr>

              <tr className="border-b border-border">
                <td className="px-4 py-3 text-sm text-text-muted">
                  {balance.insuredAddress}
                </td>
                <td className="px-4 py-3 text-sm text-text text-right">
                  {balance.insuredCityStateZip}
                </td>
              </tr>

              {/* Balance Details */}
              <tr className="border-b border-border bg-surface/50">
                <td className="px-4 py-3 text-sm text-text-muted">Current Balance</td>
                <td className="px-4 py-3 text-sm font-medium text-text text-right">
                  {formatCurrency(balance.totalBalance)}
                </td>
              </tr>

              {balance.pastDue > 0 && (
                <tr className="border-b border-border bg-danger/5">
                  <td className="px-4 py-3 text-sm font-medium text-danger">Past Due Amount</td>
                  <td className="px-4 py-3 text-sm font-medium text-danger text-right">
                    {formatCurrency(balance.pastDue)}
                  </td>
                </tr>
              )}

              {/* Payment Amount - Highlighted */}
              <tr className="bg-primary/5 border-b border-border">
                <td className="px-4 py-4 text-base font-semibold text-primary">Amount to Pay</td>
                <td className="px-4 py-4 text-xl font-bold text-primary text-right">
                  {formatCurrency(paymentAmount)}
                </td>
              </tr>

              {/* Remaining Balance */}
              <tr className="bg-surface">
                <td className="px-4 py-3 text-sm text-text-muted">Remaining After Payment</td>
                <td
                  className={cn(
                    'px-4 py-3 text-sm font-semibold text-right',
                    isPaidInFull ? 'text-success' : 'text-text'
                  )}
                >
                  {formatCurrency(Math.max(0, balance.totalBalance - paymentAmount))}
                </td>
              </tr>
            </tbody>
          </table>
        </CardBody>
      </Card>

      {/* Paid in Full Badge */}
      {isPaidInFull && (
        <Card variant="success">
          <CardBody>
            <div className="flex items-start gap-3">
              <svg
                className="h-5 w-5 text-success flex-shrink-0 mt-0.5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="text-sm font-semibold text-text">Paying in Full</p>
                <p className="text-sm text-text-muted mt-0.5">
                  Your account will be paid off after this payment is processed.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Important Notice */}
      <Card className="bg-surface">
        <CardBody>
          <p className="font-medium text-text text-sm mb-2">Important Information</p>
          <ul className="list-disc list-inside space-y-1 text-sm text-text-muted">
            <li>Payment will be processed immediately</li>
            <li>A confirmation email will be sent after processing</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
